import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useRef } from 'react';

import Button from '../../../../../components/Button';
import Footer from '../../../../../components/Relatorio/Footer';
import Header from '../../../../../components/Relatorio/Header';
import { getUser } from '../../../../../modules/contexts/Auth/RequireAuth';
import httpClient, { isHttpError } from '../../../../../services/httpClient';

interface RelatorioPDFProps {
  nomeCompleto: string;
  titulo: string;
  template: string;
  sections: Array<{
    title: string;
    data: Array<Record<string, any>>;
    fields: Array<{
      label: string;
      key: string;
    }>;
  }>;
}

// DADOS DO USUÁRIO
const user = getUser();
const userID = user?.sub;

const RelatorioPDF: React.FC<RelatorioPDFProps> = ({
  nomeCompleto,
  titulo,
  template,
  sections,
}) => {
  const relatorioRef = useRef<HTMLDivElement>(null);

  // Função para inserir o relatório na API
  const inserirRelatorioNaAPI = async (relatorioData: Record<string, any>) => {
    try {
      const response = await httpClient.post(`/api/relatorios`, relatorioData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Relatório inserido com sucesso:', response.data);
      //  alert('Relatório inserido com sucesso!');
    } catch (error) {
      if (isHttpError(error)) {
        console.error(
          'Erro ao inserir relatório:',
          error.response?.data || error.message,
        );
        alert(
          `Erro ao inserir relatório: ${error.response?.data?.message || error.message}`,
        );
      } else {
        console.error('Erro desconhecido:', error);
        alert('Erro desconhecido ao inserir relatório.');
      }
    }
  };

  // Função para gerar o PDF e inserir o relatório na API
  const gerarPDF = async () => {
    const element = relatorioRef.current;
    if (!element) return;

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let position = 0;

      while (position < imgHeight) {
        const remainingHeight = imgHeight - position;

        if (remainingHeight <= pageHeight) {
          pdf.addImage(
            imgData,
            'PNG',
            0,
            0,
            pageWidth,
            (remainingHeight * pageWidth) / canvas.width,
          );
          break;
        } else {
          pdf.addImage(imgData, 'PNG', 0, -position, pageWidth, pageHeight);
          position += pageHeight;
          pdf.addPage();
        }
      }

      // Nome dinâmico para o arquivo PDF
      const timestamp = new Date().toISOString().split('T')[0];
      const sanitizedUserName = nomeCompleto.replace(/[^a-zA-Z0-9_]/g, '_');
      const fileName = `${template.toLowerCase().replace(/\s+/g, '_')}_${sanitizedUserName}_${timestamp}.pdf`;

      pdf.save(fileName);

      // Configuração do payload para a API
      const relatorioData = {
        coUsuarioRegistro: userID, // ID do usuário que realizou o registro
        coUsuarioVisualizacao: 1, // ID do usuário que realizou a última visualização
        dtRegistro: new Date().toISOString(), // Data de registro
        dtUltimaVisualizacao: new Date().toISOString(), // Data da última visualização
        nuVisualizacao: 1, // Número total de visualizações
        nuVisualizador: 1, // Número de visualizadores únicos
        dtRelatorioGerado: new Date().toISOString().split('T')[0], // Data do relatório (YYYY-MM-DD)
        dsTitulo: titulo, // Nome do template
        dsTemplate: template, // Nome do template
        dsDadosRelatorio: JSON.stringify(sections), // Dados do relatório
        dsObservacao:
          sections.find((section) => section.title === 'Observações Gerais')
            ?.data[0]?.observacoes || 'Sem observações adicionais',
        stRegistroAtivo: 'S', // Status do registro
      };

      // Inserir o relatório na API após a geração do PDF
      await inserirRelatorioNaAPI(relatorioData);
    } catch (error) {
      console.error('Erro ao gerar o PDF:', error);
    }
  };

  return (
    <div style={{ margin: '5px' }}>
      <Button type="button" onClick={gerarPDF} label="Baixar PDF" botaoAzul />

      <div
        ref={relatorioRef}
        className="relatorio-container"
        style={{
          width: '210mm',
          minHeight: '297mm',
          background: '#fff',
          boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
          padding: '20mm',
          fontFamily: "'Open Sans', sans-serif",
          color: '#333',
        }}
      >
        {/* Header */}
        <Header nomeCompleto={nomeCompleto} template={template} />

        {/* Content */}
        <main className="content">
          {sections.map((section, index) => (
            <section
              key={index}
              className="horizontal-section log"
              style={{ marginBottom: '30px' }}
            >
              <div className="section">
                <div
                  className="section-titulo"
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: '#1351B4',
                    color: '#fff',
                    paddingBottom: '6px',
                    paddingTop: '6px',
                    marginBottom: '12px',
                    paddingLeft: '12px',
                    borderRadius: '5px',
                  }}
                >
                  {section.title}
                </div>
                <div
                  className="grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '15px',
                    marginTop: '10px',
                  }}
                >
                  {section.data.map((row, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                      {section.fields.map((field, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: '#F2F4F4',
                            border: '1px solid #B3B6B7',
                            borderRadius: '8px',
                            padding: '10px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '11px',
                              fontWeight: 'bold',
                              color: '#2C3E50',
                              marginBottom: '5px',
                            }}
                          >
                            {field.label}
                          </div>
                          <div style={{ fontSize: '13px', color: '#34495E' }}>
                            {row[field.key] || 'N/A'}
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default RelatorioPDF;
